// ── The three dailies, as one registry ───────────────────────────────────────
// Shared by the intro, the header's slate strip, the curtain's "Next up" door and
// the Home strip, so the games read as one set. Tone = the colour the shell uses
// for that game's glyph tile, glow and primary pill; the boards keep their own look.
import { Puzzle, Target, Gavel } from 'lucide-react'

export const GAMES = [
  { key: 'cliffhanger', label: 'Cliffhanger', route: '/cliffhanger', Icon: Puzzle, tone: '#d4af37', eyebrow: 'Daily case',  promise: 'One mystery show. Name it from the clues.', loading: 'Opening today’s case…' },
  { key: 'faceoff',     label: 'Face-Off',    route: '/faceoff',     Icon: Target, tone: '#f5c842', eyebrow: 'Daily five',  promise: 'Two posters, one question. Trust your gut.', loading: 'Cueing up today’s five…' },
  { key: 'verdict',     label: 'Verdict',     route: '/verdict',     Icon: Gavel,  tone: '#26c6c6', eyebrow: 'Daily bench', promise: 'One show, five calls. Graded by how close you land.', loading: 'Calling today’s case…' },
]
export const gameByKey = (key) => GAMES.find(g => g.key === key) || GAMES[0]

// Played-today markers: a tiny per-day map in localStorage. Each game sets its own
// key when a real day completes OR when the same-day guard restores a finished day,
// so the slate strip and the Next-up door are right even offline and signed out.
const KEY = (day) => `nextep_games_day_${day}`
export function playedToday(day) {
  try { return JSON.parse(localStorage.getItem(KEY(day)) || '{}') || {} } catch { return {} }
}
// `result` ({ score, denom, won }) is recorded on a LIVE finish so the Full Slate sheet
// can show all three scores; the same-day restore calls without it and must not
// overwrite a recorded result with a bare `true`.
export function markPlayed(key, day, result) {
  if (day == null) return playedToday(day)
  const prev = playedToday(day)
  const m = { ...prev, [key]: result ? { ...result, at: Date.now() } : (prev[key] || true) }
  try { localStorage.setItem(KEY(day), JSON.stringify(m)) } catch {}
  try { window.dispatchEvent(new CustomEvent('nextep:games-slate', { detail: { day, played: m } })) } catch {}
  return m
}
// First unplayed game after `key` in slate order, wrapping; null when the slate is full.
export function nextGameAfter(key, day, isEnabled = () => true) {
  const played = playedToday(day)
  const i = GAMES.findIndex(g => g.key === key)
  for (let n = 1; n <= GAMES.length; n++) {
    const g = GAMES[(i + n) % GAMES.length]
    if (g.key !== key && !played[g.key] && isEnabled(g.key)) return g
  }
  return null
}
export const slateFull = (day) => GAMES.every(g => playedToday(day)[g.key])
// Once per day: true the first time the slate becomes full.
export function claimFullSlate(day) {
  if (!slateFull(day)) return false
  const k = `nextep_full_slate_${day}`
  try { if (localStorage.getItem(k)) return false; localStorage.setItem(k, '1') } catch {}
  return true
}

// The recorded results for the day, in slate order; a bare `true` (restored, no score) reads as null.
export const slateResults = (day) => { const p = playedToday(day); return GAMES.map(g => ({ ...g, result: p[g.key] && typeof p[g.key] === 'object' ? p[g.key] : null, played: !!p[g.key] })) }

// How many days in a row the slate has been full, counting today (claimFullSlate sets
// today's key before the curtain asks). "3 days running" is what makes the sheet mean
// something beyond today; the first one reads as a first.
export function fullSlateRun(day) {
  let n = 0
  try { for (let d = day; d >= 0 && localStorage.getItem(`nextep_full_slate_${d}`); d--) n++ } catch {}
  return n
}
export const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]) }
